import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  Row,
  Col,
  message,
  Typography,
  Upload,
  Table,
  Modal,
  Descriptions,
  Popconfirm,
  Checkbox,
  Tabs,
} from "antd";
import {
  ReloadOutlined,
  UploadOutlined,
  InboxOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  getSchedulerStatus,
  startCrawlTask,
  importExcelDirect,
  getTasks,
  getTask,
  deleteTask,
  createTask,
  cancelTask,
} from "../../utils/api";
import * as XLSX from "xlsx";
import SearchShortDramaChannelsButton from "./SearchShortDramaChannelsButton.jsx";
import UpdateYoutubeQuestVideosButton from "./UpdateYoutubeQuestVideosButton.jsx";

const { Text, Title } = Typography;
const { Dragger } = Upload;

const TaskManagement = () => {
  const [taskData, setTaskData] = useState({
    lastRun: null,
    nextRun: null,
    status: "idle",
    runningTask: null, // 运行中的任务信息
    config: {},
    refresh: {
      lastRun: null,
      nextRun: null,
      status: "idle",
      runningTask: null,
    },
  });
  const [gameCrawlLoading, setGameCrawlLoading] = useState(false);
  const [refreshTaskLoading, setRefreshTaskLoading] = useState(false);

  // 任务历史记录
  const [tasks, setTasks] = useState({ list: [], total: 0 });
  const [tasksPagination, setTasksPagination] = useState({
    page: 1,
    pageSize: 10,
  });
  const [tasksLoading, setTasksLoading] = useState(false);

  // 任务详情
  const [taskDetailVisible, setTaskDetailVisible] = useState(false);
  const [currentTaskDetail, setCurrentTaskDetail] = useState(null);

  // 实时轮询
  const [pollingTaskId, setPollingTaskId] = useState(null);

  // Excel 导入 Modal
  const [excelImportVisible, setExcelImportVisible] = useState(false);
  const [excelImportStep, setExcelImportStep] = useState(1); // 1: 上传, 2: 选择Sheets
  const [uploadedExcelInfo, setUploadedExcelInfo] = useState(null);
  const [uploadedExcelFile, setUploadedExcelFile] = useState(null); // 保存文件对象
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);

  const allSheets = uploadedExcelInfo?.sheets || [];
  const isAllSheetsSelected = allSheets.length > 0 && selectedSheets.length === allSheets.length;
  const isIndeterminateSheets = selectedSheets.length > 0 && selectedSheets.length < allSheets.length;

  useEffect(() => {
    loadTaskStatus();
    // 每30秒更新一次状态
    const interval = setInterval(loadTaskStatus, 30000);
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 加载任务列表（带分页）
  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksPagination.page, tasksPagination.pageSize]);

  const loadTaskStatus = async () => {
    try {
      const data = await getSchedulerStatus();
      setTaskData({
        lastRun: data.lastRun,
        nextRun: data.nextRun,
        status: data.status || "idle",
        runningTask: data.runningTask || null,
        config: data.config || {},
        refresh: data.refresh || {
          lastRun: null,
          nextRun: null,
          status: "idle",
          runningTask: null,
        },
      });
    } catch (error) {
      console.error("加载任务状态失败:", error);
      message.error("加载任务状态失败");
    }
  };

  // 加载任务列表
  const loadTasks = async () => {
    try {
      setTasksLoading(true);
      const data = await getTasks({
        page: tasksPagination.page,
        pageSize: tasksPagination.pageSize,
      });
      setTasks(data);
    } catch (error) {
      console.error("加载任务列表失败:", error);
      message.error("加载任务列表失败");
    } finally {
      setTasksLoading(false);
    }
  };

  // 检查是否有同类型的YouTube频道数据抓取任务正在运行
  // 抓取任务：taskType === 'crawl'
  const hasRunningCrawlTask = () => {
    // 判断是否为抓取任务
    const isCrawlTask = (task) => {
      if (!task) return false;
      // 直接判断 taskType 是否为 'crawl'
      return task.taskType === 'crawl';
    };

    // 检查任务状态中的运行任务
    if (taskData.status === "running" && taskData.runningTask) {
      if (isCrawlTask(taskData.runningTask)) {
        return true;
      }
    }
    
    // 从任务列表中检查是否有运行中或待处理的抓取任务
    const activeStatuses = ['running', 'pending'];
    return tasks.list?.some(task => 
      activeStatuses.includes(task.status) && isCrawlTask(task)
    ) || false;
  };


  const handleRunTask = async () => {
    try {
      setGameCrawlLoading(true);
      const task = await startCrawlTask();
      message.success("任务已开始执行");

      // 开始轮询任务状态
      setPollingTaskId(task.id);

      await loadTaskStatus();
    } catch (error) {
      console.error("执行任务失败:", error);
      message.error("执行任务失败");
    } finally {
      setGameCrawlLoading(false);
    }
  };

  // 手动触发刷新频道信息任务
  const handleRunRefreshTask = async () => {
    try {
      setRefreshTaskLoading(true);
      const task = await createTask({ type: 'refresh' });
      message.success("刷新任务已开始执行");

      // 开始轮询任务状态
      setPollingTaskId(task.id);

      await loadTaskStatus();
    } catch (error) {
      console.error("执行刷新任务失败:", error);
      message.error("执行刷新任务失败");
    } finally {
      setRefreshTaskLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      running: { color: "processing", text: "执行中" },
      completed: { color: "success", text: "已完成" },
      success: { color: "success", text: "执行成功" },
      failed: { color: "error", text: "执行失败" },
      error: { color: "error", text: "执行错误" },
      pending: { color: "warning", text: "等待中" },
      idle: { color: "default", text: "空闲" },
      stopped: { color: "default", text: "已停止" },
    };
    const config = statusConfig[status] || statusConfig.idle;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 实时轮询任务状态
  useEffect(() => {
    if (!pollingTaskId) return;

    const pollTask = async () => {
      try {
        const task = await getTask(pollingTaskId);

        // 如果任务完成或失败，停止轮询
        if (task.status === "completed" || task.status === "failed") {
          setPollingTaskId(null);
          message[task.status === "completed" ? "success" : "error"](
            task.status === "completed"
              ? `任务完成！处理了 ${task.itemCount || 0} 个频道`
              : "任务失败，请查看详情"
          );
          // 刷新任务列表
          loadTasks();
          loadTaskStatus();
        }
      } catch (error) {
        console.error("轮询任务失败:", error);
      }
    };

    // 立即执行一次
    pollTask();

    // 每 5 秒轮询一次
    const interval = setInterval(pollTask, 5000);

    return () => clearInterval(interval);
  }, [pollingTaskId]);

  // 显示任务详情
  const showTaskDetail = async (taskId) => {
    try {
      const task = await getTask(taskId);
      setCurrentTaskDetail(task);
      setTaskDetailVisible(true);
    } catch (error) {
      console.error("加载任务详情失败:", error);
      message.error("加载任务详情失败");
    }
  };

  // 删除任务
  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      message.success("任务已删除");
      loadTasks();
    } catch (error) {
      console.error("删除任务失败:", error);
      message.error("删除任务失败");
    }
  };

  // 取消任务
  const handleCancelTask = async (taskId) => {
    try {
      await cancelTask(taskId);
      message.success("任务已标记为取消，正在停止执行...");
      // 如果正在轮询这个任务，停止轮询
      if (pollingTaskId === taskId) {
        setPollingTaskId(null);
      }
      // 刷新任务列表和状态
      loadTasks();
      loadTaskStatus();
    } catch (error) {
      console.error("取消任务失败:", error);
      message.error(`取消任务失败: ${error.message || "未知错误"}`);
    }
  };

  // 打开 Excel 导入 Modal
  const handleOpenExcelImport = () => {
    setExcelImportVisible(true);
    setExcelImportStep(1);
    setUploadedExcelInfo(null);
    setUploadedExcelFile(null);
    setSelectedSheets([]);
  };

  // 处理 Excel 上传（前端解析，不调用后端）
  const handleExcelUpload = async ({ file }) => {
    setUploadingExcel(true);
    try {
      // 使用 FileReader 读取文件
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // 获取所有 sheet 名称
          const sheets = workbook.SheetNames;
          
          // 获取每个 sheet 的信息（行数）
          const sheetInfo = sheets.map(sheetName => {
            const sheet = workbook.Sheets[sheetName];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            return {
              name: sheetName,
              rows: data.length
            };
          });
          
          setUploadedExcelInfo({
            fileName: file.name,
            sheets: sheets,
            sheetInfo: sheetInfo
          });
          setUploadedExcelFile(file); // 保存文件对象，后续导入时使用
          setSelectedSheets([]); // 重置选择
          setExcelImportStep(2); // 进入选择 Sheets 步骤
          message.success("文件解析成功");
        } catch (parseError) {
          console.error("解析Excel失败:", parseError);
          message.error("文件解析失败，请确保文件格式正确");
    } finally {
          setUploadingExcel(false);
        }
      };
      
      reader.onerror = () => {
        console.error("读取文件失败");
        message.error("读取文件失败");
        setUploadingExcel(false);
      };
      
      // 读取文件为 ArrayBuffer
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error("处理文件失败:", error);
      message.error("处理文件失败");
      setUploadingExcel(false);
    }
  };

  // 开始导入（一次性导入多个 Sheets）
  const handleStartImport = async () => {
    if (!uploadedExcelFile || selectedSheets.length === 0) {
      message.error("请至少选择一个 Sheet");
      return;
    }

    console.log(`[开始导入] 选中的 Sheets:`, selectedSheets);
    console.log(`[开始导入] Sheets 数量:`, selectedSheets.length);
    console.log(`[开始导入] 文件:`, uploadedExcelFile.name);

    setImportingExcel(true);
    
    try {
      // 一次性发送所有选中的 Sheets
      const result = await importExcelDirect(uploadedExcelFile, selectedSheets);
      
      console.log(`[导入完成] 结果:`, result);
      
      // 处理返回结果（后端返回单个任务对象）
      if (result && result.id) {
        // 开始轮询任务
        setPollingTaskId(result.id);
        
        const sheetCount = result.configSnapshot?.sheetCount || selectedSheets.length;
        const isMultiple = sheetCount > 1;
        setExcelImportVisible(false);
        loadTasks();
        
        if (isMultiple) {
          message.success(`已创建导入任务，正在处理 ${sheetCount} 个 Sheet...`);
        } else {
          message.success(`已创建导入任务，正在执行...`);
        }
      } else {
        message.error("未创建导入任务");
      }
    } catch (error) {
      console.error("创建导入任务失败:", error);
      message.error(`创建导入任务失败: ${error.message}`);
    } finally {
      setImportingExcel(false);
    }
  };


  const getFileName = (path) => {
    if (!path) return "";
    const parts = String(path).split(/[\\/]/);
    return parts[parts.length - 1] || path;
  };

  return (
    <div className="p-6">
      <Tabs defaultActiveKey="trigger" type="card">
        <Tabs.TabPane 
          tab="任务触发" 
          key="trigger"
        >
          <Card
            title={
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={5} style={{ margin: 0 }}>
                    任务触发
                  </Title>
                </Col>
                <Col>
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={() => {
                      loadTaskStatus();
                      loadTasks();
                    }}
                  >
                    刷新
                  </Button>
                </Col>
              </Row>
            }
          >
        <Card
          type="inner"
          className="mb-4"
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>YouTube频道数据抓取任务</Text>
              </Col>
            </Row>
          }
        >
          <Text type="secondary" className="block mb-4">
            自动抓取YouTube平台游戏相关频道和视频数据，更新主播排名信息
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Space>
                <Text type="secondary">执行时间:</Text>
                <Text strong>每周日凌晨 02:00</Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Text type="secondary">上次执行:</Text>
                <Text>
                  {taskData.lastRun
                    ? new Date(taskData.lastRun).toLocaleString("zh-CN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : "暂无记录"}
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Text type="secondary">下次执行:</Text>
                <Text>
                  {taskData.nextRun
                    ? new Date(taskData.nextRun).toLocaleString("zh-CN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "未安排"}
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Text type="secondary">状态:</Text>
                {getStatusTag(taskData.status)}
              </Space>
            </Col>
          </Row>

          <Row justify="end" className="mt-4">
            <Space>
              {taskData.status === "running" && taskData.runningTask &&  (
                <Text type="warning" style={{ fontSize: 12 }}>
                  有任务正在执行中（已运行 {taskData.runningTask.durationHours || 0} 小时）
                </Text>
              )}
              <Button
                onClick={handleRunTask}
                loading={gameCrawlLoading}
                disabled={hasRunningCrawlTask()}
              >
                立即抓取游戏频道信息
              </Button>
            </Space>
          </Row>
        </Card>
        <Card
          type="inner"
          className="mb-4"
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>频道信息刷新任务</Text>
              </Col>
            </Row>
          }
        >
          <Text type="secondary" className="block mb-4">
            自动刷新平台中已有频道的信息和视频数据，更新邮箱、订阅数等统计信息
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Space>
                <Text type="secondary">执行时间:</Text>
                <Text strong>每周三凌晨 02:00</Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Text type="secondary">上次执行:</Text>
                <Text>
                  {taskData.refresh?.lastRun
                    ? new Date(taskData.refresh.lastRun).toLocaleString("zh-CN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : "暂无记录"}
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Text type="secondary">下次执行:</Text>
                <Text>
                  {taskData.refresh?.nextRun
                    ? new Date(taskData.refresh.nextRun).toLocaleString("zh-CN", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "未安排"}
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Text type="secondary">状态:</Text>
                {getStatusTag(taskData.refresh?.status || "idle")}
              </Space>
            </Col>
          </Row>

          <Row justify="end" className="mt-4">
            <Space>
              {taskData.refresh?.status === "running" && taskData.refresh?.runningTask && (
                <Text type="warning" style={{ fontSize: 12 }}>
                  有刷新任务正在执行中（已运行 {taskData.refresh.runningTask.durationHours || 0} 小时）
                </Text>
              )}
              <Button
                onClick={handleRunRefreshTask}
                loading={refreshTaskLoading}
                disabled={taskData.refresh?.status === "running" || taskData.refresh?.status === "pending"}
              >
                立即刷新频道信息
              </Button>
            </Space>
          </Row>
        </Card>

        <Card
          type="inner"
          className="mb-4"
          title="Excel频道数据导入任务"
        > 
          <Row justify="space-between" align="middle">
              <Col>
                <Text type="secondary" className="block mb-4">
                  从Excel文件批量导入频道数据，支持多Sheet选择
                </Text>
              </Col>
              <Col>
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleOpenExcelImport}
                  >
                    开始导入
                  </Button>
              </Col>
            </Row>
        </Card>

        <Card
          type="inner"
          className="mb-4"
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>短剧频道搜索任务</Text>
              </Col>
              <Col>
                <Tag color="blue">手动执行</Tag>
              </Col>
            </Row>
          }
        >
          <Text type="secondary" className="block mb-4">
            通过搜索关键词自动发现和添加短剧相关的 YouTube 频道
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text type="secondary">
                功能说明：系统会根据预设的关键词搜索 YouTube 视频，自动识别短剧相关频道并添加到系统中。
                支持自定义搜索参数，包括每个关键词的返回结果数和搜索时间范围。
              </Text>
            </Col>
          </Row>

          <Row justify="end" className="mt-4">
            <SearchShortDramaChannelsButton 
              onSuccess={(data) => {
                message.success('短剧频道搜索任务已启动，请稍后刷新查看结果');
                // 刷新任务列表
                loadTasks();
              }}
            />
          </Row>
        </Card>

        <Card
          type="inner"
          className="mb-4"
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>更新YouTube Quest Videos任务</Text>
              </Col>
              <Col>
                <Tag color="blue">手动执行</Tag>
              </Col>
            </Row>
          }
        >
          <Text type="secondary" className="block mb-4">
            更新YouTube Quest Videos的统计数据，包括播放量、点赞数等实时指标
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Text type="secondary">
                功能说明：系统会更新所有类型为 'youtube-video' 且正在进行的 quests 的视频统计数据。
                可以指定 Quest ID 来更新特定任务，或留空更新所有符合条件的任务。
              </Text>
            </Col>
          </Row>

          <Row justify="end" className="mt-4">
            <UpdateYoutubeQuestVideosButton 
              onSuccess={(data) => {
                message.success('YouTube Quest Videos 更新任务已启动，请稍后刷新查看结果');
                // 刷新任务列表
                loadTasks();
              }}
            />
          </Row>
        </Card>
          </Card>
        </Tabs.TabPane>

        {/* 第二个面板：任务执行记录 */}
        <Tabs.TabPane 
          tab="任务执行记录" 
          key="records"
        >
          <Card
            title={
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={5} style={{ margin: 0 }}>
                    任务执行记录
                  </Title>
                </Col>
                <Col>
                  <Button size="small" onClick={loadTasks} loading={tasksLoading}>
                    刷新
                  </Button>
                </Col>
              </Row>
            }
          >
          <Table
            dataSource={tasks.list}
            loading={tasksLoading}
            rowKey="id"
            columns={[
              {
                title: "任务ID",
                dataIndex: "id",
                key: "id",
                width: 120,
                render: (id) => <Text code>{id.slice(0, 8)}</Text>,
              },
              {
                title: "类型",
                dataIndex: "configSnapshot",
                key: "type",
                width: 180,
                render: (config, record) => {
                  const triggerType = record.triggerType || 'manual';
                  const triggerTag = triggerType === 'auto' ? (
                    <Tag color="orange" style={{ marginRight: 4 }}>自动</Tag>
                  ) : (
                    <Tag color="default" style={{ marginRight: 4 }}>手动</Tag>
                  );
                  
                  if (record.taskType === 'refresh') {
                    return (
                      <Space>
                        {triggerTag}
                        <Tag color="purple">频道刷新</Tag>
                      </Space>
                    );
                  }
                  if (record.taskType === 'excel-import') {
                    const sheetCount = config?.sheetCount || 1;
                    return (
                      <Space>
                        {triggerTag}
                        <Tag color="blue">
                          {sheetCount > 1 ? `Excel导入(${sheetCount}个Sheet)` : "Excel导入"}
                        </Tag>
                      </Space>
                    );
                  }
                  // 默认为游戏抓取
                  return (
                    <Space>
                      {triggerTag}
                      <Tag color="green">游戏抓取</Tag>
                    </Space>
                  );
                },
              },
              {
                title: "状态",
                dataIndex: "status",
                key: "status",
                width: 100,
                render: (status) => getStatusTag(status),
              },
              {
                title: "开始时间",
                dataIndex: "startTime",
                key: "startTime",
                width: 180,
                render: (time) =>
                  time ? new Date(time).toLocaleString("zh-CN") : "-",
              },
              {
                title: "结束时间",
                dataIndex: "endTime",
                key: "endTime",
                width: 180,
                render: (time) =>
                  time ? new Date(time).toLocaleString("zh-CN") : "-",
              },
              {
                title: "处理数量",
                dataIndex: "itemCount",
                key: "itemCount",
                width: 100,
                render: (count) => `${count || 0} 个`,
              },
              {
                title: "操作",
                key: "actions",
                width: 250,
                render: (_, record) => (
              <Space>
                    <Button
                      size="small"
                      onClick={() => showTaskDetail(record.id)}
                    >
                      详情
                    </Button>
                    {["running", "pending"].includes(record.status) && (
                      <Popconfirm
                        title="确定取消此任务？"
                        description="任务将在下一个检查点停止执行"
                        onConfirm={() => handleCancelTask(record.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button size="small" danger icon={<StopOutlined />}>
                          取消
                        </Button>
                      </Popconfirm>
                    )}
                    {["completed", "failed"].includes(record.status) && (
                      <Popconfirm
                        title="确定删除此任务？"
                        onConfirm={() => handleDeleteTask(record.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button size="small" danger>
                          删除
                        </Button>
                      </Popconfirm>
                    )}
              </Space>
                ),
              },
            ]}
            pagination={{
              current: tasksPagination.page,
              pageSize: tasksPagination.pageSize,
              total: tasks.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setTasksPagination({ page, pageSize });
              },
            }}
          />
          </Card>
        </Tabs.TabPane>
      </Tabs>

      {/* Excel 导入 Modal */}
      <Modal
        title={excelImportStep === 1 ? "上传 Excel 文件" : "选择要导入的 Sheet"}
        open={excelImportVisible}
        onCancel={() => {
          setExcelImportVisible(false);
          setExcelImportStep(1);
          setUploadedExcelInfo(null);
          setUploadedExcelFile(null);
          setSelectedSheets([]);
        }}
        footer={
          excelImportStep === 1
            ? null
            : [
                <Button
                  key="back"
                  onClick={() => {
                    setExcelImportStep(1);
                    setUploadedExcelInfo(null);
                    setUploadedExcelFile(null);
                    setSelectedSheets([]);
                  }}
                >
                  重新上传
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={handleStartImport}
                  disabled={selectedSheets.length === 0 || importingExcel}
                  loading={importingExcel}
                >
                  {importingExcel ? "导入中..." : `开始导入 (${selectedSheets.length})`}
                </Button>,
              ]
        }
        width={600}
      >
        {excelImportStep === 1 ? (
          <div>
            <Dragger
              accept=".xlsx,.xls,.csv"
              beforeUpload={(file) => {
                const ok = /\.(xlsx|xls|csv)$/i.test(file.name);
                if (!ok) message.error("仅支持 Excel 文件");
                return ok || Upload.LIST_IGNORE;
              }}
              customRequest={handleExcelUpload}
              showUploadList={false}
              disabled={uploadingExcel}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {uploadingExcel ? "上传中..." : "点击或拖拽文件到此区域上传"}
              </p>
              <p className="ant-upload-hint">
                支持 .xlsx、.xls、.csv 格式的文件
              </p>
            </Dragger>
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                <strong>Excel 格式要求：</strong>
                <br />
                • 第3列（C列）必须包含 YouTube 频道 URL
                <br />• 支持的 URL 格式：
                <br />
                &nbsp;&nbsp;- https://www.youtube.com/channel/UCxxxxx
                <br />
                &nbsp;&nbsp;- https://www.youtube.com/@username
                <br />
                &nbsp;&nbsp;- https://www.youtube.com/c/ChannelName
              </Text>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>文件：</Text>
              <Text code style={{ marginLeft: 8 }}>
                {uploadedExcelInfo?.fileName ||
                  getFileName(uploadedExcelInfo?.filePath)}
              </Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>请选择要导入的 Sheet：</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                (可多选)
              </Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Checkbox
                indeterminate={isIndeterminateSheets}
                checked={isAllSheetsSelected}
                onChange={(e) =>
                  setSelectedSheets(e.target.checked ? [...allSheets] : [])
                }
              >
                全选
              </Checkbox>
            </div>
            <Checkbox.Group
              style={{ width: "100%" }}
              value={selectedSheets}
              onChange={setSelectedSheets}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                {allSheets.map((sheet) => (
                  <Card key={sheet} size="small" hoverable>
                    <Checkbox value={sheet}>
                      <Text strong>{sheet}</Text>
                      {uploadedExcelInfo?.sheetInfo && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          (
                          {uploadedExcelInfo.sheetInfo.find(
                            (s) => s.name === sheet
                          )?.rows || 0}{" "}
                          行)
                        </Text>
                      )}
                    </Checkbox>
                  </Card>
                    ))}
                  </Space>
            </Checkbox.Group>
          </div>
        )}
      </Modal>

      {/* 任务详情 Modal */}
      <Modal
        title="任务详情"
        open={taskDetailVisible}
        onCancel={() => setTaskDetailVisible(false)}
        footer={null}
        width={800}
      >
        {currentTaskDetail && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="任务ID" span={2}>
              <Text code>{currentTaskDetail.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(currentTaskDetail.status)}
            </Descriptions.Item>
            <Descriptions.Item label="类型">
              {(() => {
                const triggerType = currentTaskDetail.triggerType || 'manual';
                const triggerText = triggerType === 'auto' ? '自动' : '手动';
                
                if (currentTaskDetail.taskType === 'refresh') {
                  return `${triggerText} - 频道刷新`;
                }
                if (currentTaskDetail.taskType === 'excel-import') {
                  const sheetCount = currentTaskDetail.configSnapshot?.sheetCount || 1;
                  const sheetNames = currentTaskDetail.configSnapshot?.sheetNames;
                  if (sheetCount > 1) {
                    return `${triggerText} - Excel导入 (${sheetCount} 个 Sheet: ${sheetNames?.join(', ') || ''})`;
                  }
                  return `${triggerText} - Excel导入`;
                }
                // 默认为游戏抓取
                return `${triggerText} - 游戏抓取`;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {currentTaskDetail.startTime
                ? new Date(currentTaskDetail.startTime).toLocaleString("zh-CN")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="结束时间">
              {currentTaskDetail.endTime
                ? new Date(currentTaskDetail.endTime).toLocaleString("zh-CN")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="处理数量" span={2}>
              {currentTaskDetail.itemCount || 0} 个频道
            </Descriptions.Item>
            <Descriptions.Item label="执行日志" span={2}>
              <div
                style={{
                  maxHeight: 300,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {(currentTaskDetail.logs || []).map((log, index) => (
                  <div
                    key={index}
                    style={{
                      padding: "4px 0",
                      width: "100%",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <Text style={{ fontSize: 12 }}>
                      {log}
                    </Text>
                  </div>
                ))}
                {(!currentTaskDetail.logs ||
                  currentTaskDetail.logs.length === 0) && (
                  <Text type="secondary">暂无日志</Text>
                )}
                </div>
            </Descriptions.Item>
            <Descriptions.Item label="错误信息" span={2}>
              {currentTaskDetail.error && (
                <Text type="danger">{currentTaskDetail.error}</Text>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="配置快照" span={2}>
              <pre
                style={{
                  padding: 8,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(currentTaskDetail.configSnapshot, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default TaskManagement;
